import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Swal from "sweetalert2";
import { apiRequest, formatCurrency, formatDate, getImageUrl } from "../../../core/common/api";
import AppImage from "../../../shared/AppImage";
import { paymentMethodLabel, orderStatusTone, isGatewayMethod } from "../../../shared/Utils";

const getAvailableStatusOptions = (status?: string) => {
  const options: Record<string, string[]> = {
    Processing: ["Processing", "Shipped", "Delivered", "Cancelled"],
    Shipped: ["Shipped", "Delivered", "Cancelled"],
    Delivered: ["Delivered", "Return Requested"],
    "Return Requested": ["Return Requested", "Return Approved", "Return Rejected"],
    "Return Approved": ["Return Approved", "Refunded"],
    Cancelled: ["Cancelled", "Refunded"],
  };

  return options[status || ""] || [status || "-"];
};

const emptyTracking = {
  courierName: "",
  trackingNumber: "",
  trackingUrl: "",
  estimatedDelivery: "",
  note: "",
};

const toTrackingForm = (tracking?: any) => ({
  courierName: tracking?.courierName || "",
  trackingNumber: tracking?.trackingNumber || "",
  trackingUrl: tracking?.trackingUrl || "",
  estimatedDelivery: tracking?.estimatedDelivery
    ? new Date(tracking.estimatedDelivery).toISOString().slice(0, 10)
    : "",
  note: tracking?.note || "",
});

const OrderDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [order, setOrder] = useState<any>(null);
  const [feedback, setFeedback] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [trackingForm, setTrackingForm] = useState(emptyTracking);
  const [isSavingTracking, setIsSavingTracking] = useState<boolean>(false);

  const fetchOrderDetail = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);

    try {
      const result = await apiRequest(`/orders/${id}`, "GET");
      if (result.success && result.order) {
        setOrder(result.order);
        setTrackingForm(toTrackingForm(result.order.tracking));
        setFeedback(result.feedback || null);
      } else {
        throw new Error(result.message || "Order not found");
      }
    } catch (error: any) {
      Swal.fire({
        title: "Error Loading Order",
        text: error.message || "Unable to fetch order details",
        icon: "error",
      });
      navigate("/orders");
    } finally {
      setIsLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchOrderDetail();
  }, [fetchOrderDetail]);

  const updateStatus = async (newStatus: string) => {
    if (!order || order.orderStatus === newStatus) return;

    if (newStatus === "Cancelled") {
      await handleCancelOrder();
      return;
    }

    if (newStatus === "Return Approved" || newStatus === "Return Rejected") {
      await handleProcessReturn(newStatus === "Return Approved" ? "approve" : "reject");
      return;
    }

    if (newStatus === "Refunded") {
      await handleProcessRefund();
      return;
    }

    let reason = "";
    if (newStatus === "Return Requested") {
      const response = await Swal.fire({
        title: "Request return?",
        input: "textarea",
        inputLabel: "Return reason",
        inputPlaceholder: "Write reason...",
        showCancelButton: true,
        confirmButtonText: "Request Return",
      });

      if (!response.isConfirmed) return;
      reason = response.value || "Requested by admin";
    }

    setIsUpdating(true);

    try {
      const result = await apiRequest(`/orders/${order._id}`, "PUT", {
        status: newStatus,
        ...(reason && { reason }),
      });

      if (result.success) {
        setOrder(result.order);
        Swal.fire({
          title: "Status Updated",
          text: `Order status set to ${newStatus}`,
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
      }
    } catch (error: any) {
      Swal.fire("Update Failed", error.message || "Failed to update status", "error");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!order) return;

    const response = await Swal.fire({
      title: "Cancel Order?",
      text: "Provide a reason for cancelling this order:",
      input: "textarea",
      inputPlaceholder: "Enter reason (e.g., Customer requested, Out of stock...)",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Cancel Order",
      confirmButtonColor: "#ef4444",
    });

    if (!response.isConfirmed) return;

    setIsUpdating(true);
    try {
      const result = await apiRequest(`/orders/${order._id}/cancel`, "PUT", {
        reason: response.value || "Cancelled by admin",
      });

      if (result.success) {
        setOrder(result.order);
        Swal.fire("Cancelled", "Order has been cancelled", "success");
      }
    } catch (error: any) {
      Swal.fire("Cancel Failed", error.message || "Order cancellation failed", "error");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleProcessReturn = async (action: "approve" | "reject") => {
    if (!order) return;

    const response = await Swal.fire({
      title: action === "approve" ? "Approve Return?" : "Reject Return?",
      text: `Enter admin note for ${action === "approve" ? "approving" : "rejecting"} return:`,
      input: "textarea",
      inputPlaceholder: "Optional note...",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: action === "approve" ? "Approve Return" : "Reject Return",
      confirmButtonColor: action === "approve" ? "#22c55e" : "#ef4444",
    });

    if (!response.isConfirmed) return;

    setIsUpdating(true);
    try {
      const result = await apiRequest(`/orders/${order._id}/return`, "PUT", {
        action,
        adminNote: response.value || "",
      });

      if (result.success) {
        setOrder(result.order);
        Swal.fire(
          action === "approve" ? "Return Approved" : "Return Rejected",
          `Return request has been ${action === "approve" ? "approved" : "rejected"}.`,
          "success",
        );
      }
    } catch (error: any) {
      Swal.fire("Return Update Failed", error.message || "Action failed", "error");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleProcessRefund = async () => {
    if (!order) return;

    const response = await Swal.fire({
      title: "Process Order Refund",
      html: `
        <div style="text-align:left; font-size:14px; color:#475569; margin-bottom:12px;">
          <p><strong>Order Total:</strong> ${formatCurrency(order.totalPrice)}</p>
          <p><strong>Payment Status:</strong> ${order.paymentInfo?.status || "Pending"}</p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Issue Refund",
      confirmButtonColor: "#1e3a6e",
    });

    if (!response.isConfirmed) return;

    setIsUpdating(true);
    try {
      const result = await apiRequest(`/orders/${order._id}/refund`, "PUT", {
        status: "Completed",
      });

      if (result.success) {
        setOrder(result.order);
        Swal.fire("Refund Updated", result.message || "Refund status updated", result.order?.refundInfo?.status === "Completed" ? "success" : "info");
      }
    } catch (error: any) {
      Swal.fire("Refund Failed", error.message || "Failed to process refund", "error");
    } finally {
      setIsUpdating(false);
    }
  };

  const changeTracking = (field: keyof typeof emptyTracking, value: string) => {
    setTrackingForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveTracking = async () => {
    if (!order) return;

    setIsSavingTracking(true);
    try {
      const result = await apiRequest(`/orders/${order._id}/tracking`, "PUT", trackingForm);
      if (result.success) {
        setOrder(result.order);
        setTrackingForm(toTrackingForm(result.order.tracking));
        Swal.fire({
          title: "Tracking Saved",
          text: "Customer can now see these details in the app",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
      }
    } catch (error: any) {
      Swal.fire("Save Failed", error.message || "Failed to save tracking", "error");
    } finally {
      setIsSavingTracking(false);
    }
  };

  const handlePrintInvoice = () => {
    window.print();
  };

  if (isLoading) {
    return (
      <div className="page-section loading-state">
        <h2>Loading Order Details…</h2>
      </div>
    );
  }

  if (!order) return null;

  const steps = ["Processing", "Shipped", "Delivered"];
  const currentStepIndex = steps.indexOf(order.orderStatus);
  const isTerminalState = ["Cancelled", "Return Requested", "Return Approved", "Return Rejected", "Refunded"].includes(order.orderStatus);

  const statusOptions = getAvailableStatusOptions(order.orderStatus);
  const canUpdate = statusOptions.length > 1;
  const canCancel = !["Delivered", "Cancelled", "Refunded"].includes(order.orderStatus);
  const canProcessReturn = order.orderStatus === "Return Requested" && order.returnRequest?.status === "Requested";
  const canRefund = ["Cancelled", "Return Approved"].includes(order.orderStatus);
  const isTrackingLocked = ["Cancelled", "Refunded"].includes(order.orderStatus);

  return (
    <div className="detail-page">
      <div className="page-hdr">
        <div className="page-hdr-left">
          <Link to="/orders" className="btn-sm btn-outline back-link">
            ← Back to Orders List
          </Link>
          <div className="page-eyebrow">Order Reference</div>
          <h1 className="page-title">
            Order #{order.orderCode || order._id?.toUpperCase()}
          </h1>
          <div className="page-sub">
            Placed on {formatDate(order.createdAt)}
          </div>
        </div>

        <div className="page-hdr-right">
          <button type="button" onClick={handlePrintInvoice} className="btn-sm btn-outline">
            Print Invoice
          </button>

          {canUpdate && (
            <select
              className="header-select"
              value={order.orderStatus}
              disabled={isUpdating}
              onChange={(e) => updateStatus(e.target.value)}
            >
              {statusOptions.map((status) => (
                <option key={status} value={status}>
                  Status: {status}
                </option>
              ))}
            </select>
          )}

          
        </div>
      </div>

      <section className="table-card progress-card">
        <h3 className="progress-title">Order Progress Status</h3>

        {!isTerminalState ? (
          <div className="progress-timeline">
            {steps.map((step, idx) => {
              const isCompleted = currentStepIndex >= idx;
              const isCurrent = currentStepIndex === idx;

              return (
                <div key={step} className="progress-step">
                  <div className={`progress-step-dot ${isCompleted ? "is-completed" : ""} ${isCurrent ? "is-current" : ""}`}>
                    {isCompleted ? "✓" : idx + 1}
                  </div>
                  <div className={`progress-step-label ${isCurrent ? "is-current" : ""}`}>
                    {step}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className={`terminal-banner ${order.orderStatus === "Cancelled" ? "is-danger" : "is-success"}`}>
            <div>
              <span className={`status-badge ${orderStatusTone(order.orderStatus)}`}>
                {order.orderStatus}
              </span>
              <p>
                {order.cancellation?.reason && `Reason: ${order.cancellation.reason}`}
                {order.returnRequest?.reason && `Return Reason: ${order.returnRequest.reason}`}
              </p>
            </div>

            <div className="terminal-actions">
              {canProcessReturn && (
                <>
                  <button type="button" onClick={() => handleProcessReturn("approve")} className="btn-sm btn-success">
                    Approve Return
                  </button>
                  <button type="button" onClick={() => handleProcessReturn("reject")} className="btn-sm btn-danger">
                    Reject Return
                  </button>
                </>
              )}

              {canRefund && (
                <button type="button" onClick={handleProcessRefund} className="btn-sm btn-outline">
                  Process Refund
                </button>
              )}
            </div>
          </div>
        )}
      </section>

      <section className="table-card">
        <div className="table-head">
          <h2>Shipment Tracking</h2>
          <p>Courier, AWB number and tracking link for the customer app</p>
        </div>
        <div className="admin-card-body page-stack">
          <div className="tracking-summary">
            <div className="tracking-summary-item">
              <span>Courier</span>
              <strong>{order.tracking?.courierName || "—"}</strong>
            </div>
            <div className="tracking-summary-item">
              <span>Tracking ID</span>
              <strong>{order.tracking?.trackingNumber || "—"}</strong>
            </div>
            <div className="tracking-summary-item">
              <span>ETA</span>
              <strong>
                {order.tracking?.estimatedDelivery
                  ? new Date(order.tracking.estimatedDelivery).toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                      timeZone: "UTC",
                    })
                  : "—"}
              </strong>
            </div>
            <div className="tracking-summary-item">
              <span>Track Link</span>
              <strong>
                {order.tracking?.trackingUrl ? (
                  <a href={order.tracking.trackingUrl} target="_blank" rel="noopener noreferrer" className="order-code">
                    Open
                  </a>
                ) : (
                  "—"
                )}
              </strong>
            </div>
          </div>

          {isTrackingLocked ? (
            <small className="form-hint">Tracking cannot be edited for {order.orderStatus.toLowerCase()} orders.</small>
          ) : (
            <>
              <div className="admin-form-grid">
                <div className="form-field">
                  <label className="modal-label">Courier Name</label>
                  <input
                    className="admin-input"
                    value={trackingForm.courierName}
                    maxLength={100}
                    onChange={(e) => changeTracking("courierName", e.target.value)}
                    placeholder="Delhivery, Blue Dart..."
                  />
                </div>
                <div className="form-field">
                  <label className="modal-label">Tracking / AWB Number</label>
                  <input
                    className="admin-input"
                    value={trackingForm.trackingNumber}
                    maxLength={100}
                    onChange={(e) => changeTracking("trackingNumber", e.target.value)}
                    placeholder="AWB number"
                  />
                </div>
                <div className="form-field">
                  <label className="modal-label">Tracking URL</label>
                  <input
                    type="url"
                    className="admin-input"
                    value={trackingForm.trackingUrl}
                    maxLength={500}
                    onChange={(e) => changeTracking("trackingUrl", e.target.value)}
                    placeholder="https://..."
                  />
                </div>
                <div className="form-field">
                  <label className="modal-label">Estimated Delivery</label>
                  <input
                    type="date"
                    className="admin-input"
                    value={trackingForm.estimatedDelivery}
                    onChange={(e) => changeTracking("estimatedDelivery", e.target.value)}
                  />
                </div>
              </div>
              <div className="form-field">
                <label className="modal-label">Note</label>
                <textarea
                  className="admin-input"
                  rows={2}
                  value={trackingForm.note}
                  maxLength={500}
                  onChange={(e) => changeTracking("note", e.target.value)}
                  placeholder="Optional note for this shipment"
                />
              </div>
              <div>
                <button
                  type="button"
                  className="btn-sm btn-primary"
                  disabled={isSavingTracking}
                  onClick={handleSaveTracking}
                >
                  {isSavingTracking ? "Saving..." : "Save Tracking"}
                </button>
              </div>
            </>
          )}
        </div>
      </section>

      <div className="detail-grid-3">
        <div className="table-card info-card">
          <h3 className="info-card-title">Customer Information</h3>
          <div className="info-list">
            <p><strong>Name:</strong> {order.user?.name || order.shippingInfo?.fullName || "Guest Customer"}</p>
            <p><strong>Email:</strong> {order.user?.email || "Not Provided"}</p>
            <p><strong>Phone:</strong> {order.shippingInfo?.phoneNo || order.user?.phone || "N/A"}</p>
            {order.user?._id && (
              <p>
                <strong>Account:</strong>{" "}
                <Link to={`/users/${order.user._id}`} className="order-code">
                  View Profile
                </Link>
              </p>
            )}
          </div>
        </div>

        <div className="table-card info-card">
          <h3 className="info-card-title">Shipping Details</h3>
          <div className="info-list">
            <p><strong>Recipient:</strong> {order.shippingInfo?.fullName || order.user?.name || "-"}</p>
            <p><strong>Address:</strong> {order.shippingInfo?.address || "-"}</p>
            <p><strong>City / State:</strong> {order.shippingInfo?.city || "-"}, {order.shippingInfo?.state || "-"}</p>
            <p><strong>Pincode / Country:</strong> {order.shippingInfo?.pinCode || "-"}, {order.shippingInfo?.country || "India"}</p>
            <p><strong>Contact Phone:</strong> {order.shippingInfo?.phoneNo || "-"}</p>
          </div>
        </div>

        <div className="table-card info-card">
          <h3 className="info-card-title">Payment Breakdown</h3>
          <div className="info-list">
            <p>
              <strong>Payment Method:</strong> {paymentMethodLabel(order.paymentInfo?.method)}
              {order.paymentInfo?.gatewayDetails?.method ? ` (${order.paymentInfo.gatewayDetails.method})` : ""}
            </p>
            <p>
              <strong>Payment Status:</strong>{" "}
              <span className={`status-badge ${order.paymentInfo?.status === "Completed" ? "success" : order.paymentInfo?.status === "Failed" ? "danger" : "warning"}`}>
                {order.paymentInfo?.status || "Pending"}
              </span>
            </p>
            <p><strong>Transaction ID:</strong> <small className="table-muted">{order.paymentInfo?.id || "N/A"}</small></p>
            <p><strong>Items Price:</strong> {formatCurrency(order.itemsPrice || order.totalPrice)}</p>
            <p><strong>Tax Price:</strong> {formatCurrency(order.taxPrice || 0)}</p>
            <p><strong>Shipping Price:</strong> {formatCurrency(order.shippingPrice || 0)}</p>
            {Number(order.walletPay?.eWallet || 0) > 0 && (
              <p><strong>Paid from eWallet:</strong> {formatCurrency(order.walletPay.eWallet)}</p>
            )}
            {Number(order.walletPay?.repurchaseFund || 0) > 0 && (
              <p>
                <strong>Paid from Re-purchases:</strong>{" "}
                {formatCurrency(order.walletPay.repurchaseFund)}
              </p>
            )}
            <p>
              <strong>
                {order.paymentInfo?.method === "cod"
                  ? "COD amount"
                  : isGatewayMethod(order.paymentInfo?.method)
                    ? `${paymentMethodLabel(order.paymentInfo?.method)} amount`
                    : "Payment Amount"}
                :
              </strong>{" "}
              {formatCurrency(Number(order.paymentInfo?.amount || 0))}
            </p>
            {Number(order.coupon?.discountAmount || 0) > 0 && (
              <p className="info-discount">
                <strong>Discount Coupon ({order.coupon?.code}):</strong> -{formatCurrency(order.coupon.discountAmount)}
              </p>
            )}
            <hr />
            <p className="info-total">
              <strong>Grand Total:</strong> {formatCurrency(order.totalPrice)}
            </p>
          </div>
        </div>
      </div>

      {order.deliveredAt && (
        <section className="table-card info-card">
          <h3 className="info-card-title">Customer Feedback</h3>
          <div className="info-list">
            {feedback ? (
              <>
                <p>
                  <strong>Rating:</strong>{" "}
                  <span className={`status-badge ${feedback.rating >= 4 ? "success" : feedback.rating <= 2 ? "danger" : "warning"}`}>
                    <span aria-hidden="true">⭐</span> {feedback.rating}/5
                  </span>
                </p>
                <p><strong>Comment:</strong> {feedback.comment || "-"}</p>
                <p><strong>Submitted:</strong> {formatDate(feedback.createdAt)}</p>
              </>
            ) : (
              <p className="table-muted">Customer has not shared feedback for this order yet.</p>
            )}
          </div>
        </section>
      )}

      {(order.createdMembers || []).length > 0 && (
        <section className="table-card">
          <div className="table-head">
            <h2>Created Members ({order.createdMembers.length})</h2>
            <p>Member IDs generated from this order (sale IDs cannot log in)</p>
          </div>
          <div className="orders-table-scroll">
            <table className="orders-table">
              <thead>
                <tr>
                  <th>Member ID</th>
                  <th>Name</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {order.createdMembers.map((member: any, idx: number) => {
                  const userId = member.user?._id || member.user;
                  return (
                    <tr key={member._id || member.memberId || idx}>
                      <td>
                        <strong>{member.memberId || member.user?.memberId || "—"}</strong>
                      </td>
                      <td>{member.user?.name || "—"}</td>
                      <td>
                        {userId ? (
                          <Link to={`/sale-users/${userId}`} className="btn-sm btn-outline">
                            View
                          </Link>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className="table-card">
        <div className="table-head">
          <h2>Order Items ({order.orderItems?.length || 0})</h2>
          <p>Product list included in this order</p>
        </div>

        <div className="orders-table-scroll">
          <table className="orders-table">
            <thead>
              <tr>
                <th>Item</th>
                <th>Price</th>
                <th>Quantity</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {(order.orderItems || []).map((item: any, idx: number) => (
                <tr key={item._id || item.product || idx}>
                  <td>
                    <div className="img-name-cell">
                      <AppImage
                        src={getImageUrl(item.image)}
                        alt={item.name}
                        crossOrigin="anonymous"
                      />
                      <div>
                        <strong>{item.name}</strong>
                        {(item.color || item.size || item.packSize) && (
                          <small className="table-muted">
                            {[
                              item.color && `Colour: ${item.color}`,
                              item.size && `Size: ${item.size}`,
                              item.packSize && `Quantity: ${item.packSize}`,
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </small>
                        )}
                        {item.product && (
                          <small className="table-muted">Product ID: {item.product}</small>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>{formatCurrency(item.price)}</td>
                  <td><strong>x{item.quantity}</strong></td>
                  <td>
                    <strong>{formatCurrency(Number(item.price || 0) * Number(item.quantity || 1))}</strong>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {(order.returnRequest?.reason || order.refundInfo?.amount || order.cancellation?.reason) && (
        <section className="table-card info-card">
          <h3 className="info-card-title">Return & Refund Summary</h3>
          <div className="detail-grid-3">
            {order.cancellation?.reason && (
              <div>
                <strong>Cancellation Detail:</strong>
                <p className="text-muted">Reason: {order.cancellation.reason}</p>
                <p className="text-muted">Cancelled At: {formatDate(order.cancellation.cancelledAt)}</p>
              </div>
            )}
            {order.returnRequest?.reason && (
              <div>
                <strong>Return Request:</strong>
                <p className="text-muted">Status: {order.returnRequest.status}</p>
                <p className="text-muted">Reason: {order.returnRequest.reason}</p>
                {order.returnRequest.adminNote && <p className="text-muted">Note: {order.returnRequest.adminNote}</p>}
              </div>
            )}
            {order.refundInfo && (
              <div>
                <strong>Refund Information:</strong>
                <p className="text-muted">Status: {order.refundInfo.status || "Pending"}</p>
                <p className="text-muted">Amount: {formatCurrency(order.refundInfo.amount)}</p>
                {order.refundInfo.refundId && <p className="text-muted">Refund ID: {order.refundInfo.refundId}</p>}
              </div>
            )}
          </div>
        </section>
      )}

      <div className="detail-footer">
        {canCancel && (
          <button type="button" onClick={handleCancelOrder} className="btn-sm btn-danger">
            Cancel Order
          </button>
        )}

        <div className="detail-footer-right">
          <Link to="/orders" className="btn-sm btn-outline">
            Close & Return
          </Link>
        </div>
      </div>
    </div>
  );
};

export default OrderDetail;
